// Lib
import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { identity, constant } from 'lodash';
import { compose } from '../../../node_module_clones/recompose';

// Utils
import dontUpdateForKeys from '../../utils/milanoteRecompose/dontUpdateForKeys';
import { gridSizeSelector } from '../../utils/grid/gridSizeSelector';
import { getCaption, getElementId, getShowCaption } from '../../../common/elements/utils/elementPropertyUtils';
import { getElementFormattingContent } from '../utils/elementUtil';
import { isHeightRestricted } from '../resizing/resizableEditor/resizableEditorUtils';
import { getIsFeatureEnabled } from '../feature/elementFeatureUtils';

// Components
import newElementDecorator from '../newElementDecorator';
import elementLineEdgeDropTarget from '../line/elementLineEdgeDropTarget';
import captionContainerDecorator from '../../components/caption/captionContainerDecorator';
import resizableEditorSaveHeightDecorator, {
    CARD_MIN_WIDTH,
} from '../resizing/resizableEditor/ResizableEditorSaveHeightDecorator';
import cardClipperStateManager from './clipper/cardClipperStateManager';
import groupResizeStateDecorator from '../resizing/groupResizeStateDecorator';
import groupResizeBehaviourDecorator from '../resizing/groupResizeBehaviourDecorator';

// Actions
import { updateElementIfNotDeleted, createAndEditElement } from '../actions/elementActions';
import { moveElementsToTrash } from '../actions/elementShortcutActions';
import { analyticsEvent } from '../../analytics';
import { updateElementType } from './universalCardActions';
import {
    getNextClipperOperationCompleteTransactionId,
    getNextRedoClipperOperationCompleteTransactionId,
    getNextRedoTransactionId,
    getNextUndoTransactionId,
    redoAction,
    undoAction,
} from '../../utils/undoRedo/undoRedoActions';

// Constants
import { ElementType } from '../../../common/elements/elementTypes';
import { MaintainAspectRatioMode } from '../resizing/resizeTypes';
import { ELEMENT_FEATURES } from '../feature/elementFeatureConstants';
import {
    EDITOR_SINGLE_LINE_HEIGHT_GRID_UNITS,
    SINGLE_LINE_CARD_MIN_HEIGHT_GRID_UNITS,
} from '../../../common/cards/cardConstants';

// Styles
import Card from './Card';

const mapDispatchToProps = (dispatch) => ({
    dispatchSaveTextContent: ({ id, textContent, ...rest }) =>
        dispatch(updateElementIfNotDeleted({ id, changes: { textContent }, ...rest })),
    dispatchCreateAndEditNewElement: ({ location, currentBoardId, element, creationSource, content }) => {
        analyticsEvent('created-card-cmd-return');

        const caption = getCaption(element);
        const cardFormatting = getElementFormattingContent(element);

        return dispatch(
            createAndEditElement({
                creationSource,
                elementType: ElementType.CARD_TYPE,
                location,
                currentBoardId,
                content: {
                    ...cardFormatting,
                    caption: caption && caption.toJS(),
                    showCaption: getShowCaption(element),
                    ...content,
                },
                select: true,
            }),
        );
    },
    dispatchMoveElementsToTrash: ({ currentBoardId, elementIds }) =>
        dispatch(moveElementsToTrash({ currentBoardId, elementIds })),
    dispatchUpdateElementType: (args) => dispatch(updateElementType(args)),
    onAppUndo: () => {
        const nextUndoTransactionId = dispatch(getNextUndoTransactionId());
        const nextUndoClipperOperationTransactionId = dispatch(getNextClipperOperationCompleteTransactionId());

        if (nextUndoTransactionId && nextUndoClipperOperationTransactionId === nextUndoTransactionId) {
            dispatch(undoAction());
        }
    },
    onAppRedo: () => {
        const nextRedoTransactionId = dispatch(getNextRedoTransactionId());
        const nextRedoClipperOperationTransactionId = dispatch(getNextRedoClipperOperationCompleteTransactionId());

        if (nextRedoTransactionId && nextRedoClipperOperationTransactionId === nextRedoTransactionId) {
            dispatch(redoAction());
        }
    },
});

const MULTI_LINE_CARD_MIN_HEIGHT_GRID_UNITS = 8.6;
const CARD_EDITOR_PADDING_GRID_UNITS = 3.8;
const getMinContainerHeight = (element, gridSize, currentEditorHeight) => {
    const isTransparencyEnabled = getIsFeatureEnabled(ELEMENT_FEATURES.TRANSPARENT)(element);

    // Because we don't want the "read more" to cover the single line of text, we need to figure out
    // if there is more than one line of text and change the minimum possible container height.
    const singleLineEditorHeight = EDITOR_SINGLE_LINE_HEIGHT_GRID_UNITS * gridSize;
    const multipleEditorLines = currentEditorHeight > singleLineEditorHeight;

    return isTransparencyEnabled || !multipleEditorLines
        ? SINGLE_LINE_CARD_MIN_HEIGHT_GRID_UNITS * gridSize
        : MULTI_LINE_CARD_MIN_HEIGHT_GRID_UNITS * gridSize;
};

const resizeDecorators = compose(
    groupResizeStateDecorator(
        constant(MaintainAspectRatioMode.NEVER),
        CARD_MIN_WIDTH,
        SINGLE_LINE_CARD_MIN_HEIGHT_GRID_UNITS,
    ),
    resizableEditorSaveHeightDecorator({
        getMinContainerHeight,
        containerPaddingGu: CARD_EDITOR_PADDING_GRID_UNITS,
    }),
    cardClipperStateManager,
    groupResizeBehaviourDecorator(),
);

@connect(gridSizeSelector, mapDispatchToProps)
@elementLineEdgeDropTarget
@dontUpdateForKeys(['dragModifierKeys'])
@captionContainerDecorator
@resizeDecorators
@newElementDecorator
class CardContainer extends React.Component {
    saveContent = (textContent, actionArgs) => {
        const { isEditable, element, dispatchSaveTextContent } = this.props;
        if (!isEditable) return;

        dispatchSaveTextContent({ id: getElementId(element), textContent, ...actionArgs });
    };

    moveElementsToTrash = () => {
        const { currentBoardId, element, dispatchMoveElementsToTrash } = this.props;
        dispatchMoveElementsToTrash({ currentBoardId, elementIds: [getElementId(element)] });
    };

    updateElementType = ({ elementType, data }) => {
        const { element, dispatchUpdateElementType } = this.props;
        dispatchUpdateElementType({ id: getElementId(element), elementType, data });
    };

    shouldComponentUpdate(nextProps) {
        return !nextProps.isElementOpenInModal || !this.props.isElementOpenInModal;
    }

    componentDidUpdate(prevProps) {
        const { setShouldPreventMeasure, forceMeasure, getShouldMeasure, element, isEditing, isResizing } = this.props;

        if (prevProps.element === element && prevProps.isEditing === isEditing && prevProps.isResizing === isResizing)
            return;

        // We don't want connected lines to keep jumping while editing cards that are height restricted
        // (because the cards expand when they're being edited)
        const newShouldMeasure = !(isHeightRestricted(element) && isEditing && !isResizing);
        if (newShouldMeasure === getShouldMeasure()) return;

        setShouldPreventMeasure(!newShouldMeasure);
        if (!newShouldMeasure) return;

        forceMeasure();
    }

    render() {
        const { connectLineEdgeDropTarget, canConnectLineEdge, createNewElement } = this.props;

        const lineEdgeConnector =
            connectLineEdgeDropTarget && canConnectLineEdge ? connectLineEdgeDropTarget : identity;

        return lineEdgeConnector(
            <div>
                <Card
                    {...this.props}
                    updateElementType={this.updateElementType}
                    saveContent={this.saveContent}
                    createNewCard={createNewElement}
                    moveElementsToTrash={this.moveElementsToTrash}
                />
            </div>,
        );
    }
}

CardContainer.propTypes = {
    element: PropTypes.object.isRequired,
    elementId: PropTypes.string,
    currentBoardId: PropTypes.string.isRequired,
    createNewElement: PropTypes.func,
    inList: PropTypes.string,
    canConnectLineEdge: PropTypes.bool,
    gridSize: PropTypes.number,
    isElementOpenInModal: PropTypes.bool,
    connectDropTarget: PropTypes.func,
    dispatchSaveTextContent: PropTypes.func,
    dispatchMoveElementsToTrash: PropTypes.func,
    isEditable: PropTypes.bool,
    connectLineEdgeDropTarget: PropTypes.func,
    dispatchUpdateElementType: PropTypes.func,
    setShouldPreventMeasure: PropTypes.func,
    isEditing: PropTypes.bool,
    isResizing: PropTypes.bool,
    forceMeasure: PropTypes.func,
    getShouldMeasure: PropTypes.func,
};

export default CardContainer;
